import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { loginUser, registerUser, formatApiErrorDetail, createOAuthState } from "@/lib/api";
import { Eye, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";

export default function AuthPage({ mode }) {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = isRegister
        ? { email: form.email, password: form.password, name: form.name }
        : { email: form.email, password: form.password };
      const user = isRegister ? await registerUser(payload) : await loginUser(payload);
      setUser(user);
      toast.success(isRegister ? t("register_success") : t("login_success"));
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Il percorso di redirect resta invariato; viene solo aggiunto il nonce `state`.
    // Conservato in sessionStorage (che un sito terzo non può scrivere), è ciò che
    // impedisce a un link `#session_id=` inviato da altri di autenticare questo browser
    // nell'account di chi ha mandato il link.
    let state = "";
    try {
      state = await createOAuthState();
      sessionStorage.setItem("vs_oauth_state", state);
    } catch {
      toast.error(t("google_login_failed"));
      return;
    }
    const redirectUrl = `${window.location.origin}/app?state=${encodeURIComponent(state)}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-10" data-testid="auth-page">
      <button onClick={() => navigate("/")} className="flex items-center gap-2.5 mb-8" data-testid="auth-brand-home">
        <span className="w-10 h-10 rounded-md bg-primary text-white flex items-center justify-center">
          <Eye className="w-5 h-5" strokeWidth={2} />
        </span>
        <span className="leading-tight text-left">
          <span className="block text-lg font-bold tracking-tight">VisioScreening</span>
          <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">{t("brand_sub")}</span>
        </span>
      </button>

      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
          {isRegister ? t("auth_register_title") : t("auth_login_title")}
        </h1>
        <p className="mt-1.5 text-sm text-slate-500">{isRegister ? t("auth_register_sub") : t("auth_login_sub")}</p>

        <Button variant="outline" onClick={googleLogin} data-testid="google-login-btn"
          className="mt-6 w-full h-11 font-semibold gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
          {t("google_btn")}
        </Button>

        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> {t("or_email")} <span className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("name_label")}</Label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="auth-name-input" className="pl-9" value={form.name}
                  onChange={(e) => setF({ name: e.target.value })} placeholder={t("name_ph")} />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("email_label")}</Label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="auth-email-input" type="email" required className="pl-9" value={form.email}
                onChange={(e) => setF({ email: e.target.value })} placeholder={t("email_ph")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("password_label")}</Label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="auth-password-input" type="password" required minLength={6} className="pl-9" value={form.password}
                onChange={(e) => setF({ password: e.target.value })} placeholder={isRegister ? t("password_ph_reg") : t("password_ph_login")} />
            </div>
            {!isRegister && (
              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-primary font-semibold underline" data-testid="forgot-password-link">{t("forgot_link")}</Link>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

          <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
            className="w-full h-11 text-base font-semibold bg-primary hover:bg-primary/90">
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("wait")}</> : (isRegister ? t("submit_register") : t("submit_login"))}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          {isRegister ? (
            <>{t("have_account")} <Link to="/login" className="text-primary font-semibold underline" data-testid="switch-to-login">{t("login_link")}</Link></>
          ) : (
            <>{t("no_account")} <Link to="/register" className="text-primary font-semibold underline" data-testid="switch-to-register">{t("register_link")}</Link></>
          )}
        </p>
      </div>
    </div>
  );
}
